import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, TrendingDown, Target, HelpCircle, ArrowLeft, BarChart2 } from 'lucide-react';
import styled, { keyframes, css } from 'styled-components';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const pulse = keyframes`0%,100%{opacity:.5}50%{opacity:1}`;

const PageWrap = styled.div`
  min-height: 100vh;
  background: #0b0c10;
  color: #c5c6c7;
  font-family: 'Inter', sans-serif;
  padding: 24px;
`;
const Header = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  border-bottom: 1px solid #1f2833; padding-bottom: 20px; margin-bottom: 24px;
`;
const TitleInfo = styled.div`
  display: flex; align-items: center; gap: 12px;
`;
const Title = styled.h1`
  font-size: 24px; font-weight: 800; color: #66fcf1; margin: 0;
  display: flex; align-items: center; gap: 10px;
`;
const Btn = styled.button`
  display: flex; align-items: center; gap: 8px;
  background: ${p=>p.primary ? '#45a29e' : 'transparent'};
  color: ${p=>p.primary ? '#0b0c10' : '#45a29e'};
  border: 1px solid #45a29e;
  padding: 8px 16px; border-radius: 8px; font-weight: 600; cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #66fcf1; color: #0b0c10; border-color: #66fcf1; }
`;
const InfoBox = styled.div`
  background: rgba(69,162,158,0.1); border: 1px solid rgba(102,252,241,0.2);
  padding: 16px; border-radius: 12px; margin-bottom: 24px;
  display: flex; align-items: flex-start; gap: 12px;
  p { margin: 0; color: #c5c6c7; font-size: 13px; line-height: 1.5; }
`;
const CardGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 20px;
`;
const Card = styled.div`
  background: #1f2833; border-radius: 16px; padding: 20px;
  border: 1px solid #2a3441;
  animation: ${fadeIn} 0.4s ease;
  transition: all 0.2s;
  cursor: pointer;
  &:hover { border-color: #45a29e; transform: translateY(-3px); box-shadow: 0 10px 20px rgba(0,0,0,0.2); }
`;
const CardHead = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;
`;
const StockName = styled.h3`
  font-size: 18px; font-weight: 700; color: #fff; margin: 0;
`;
const SpringTag = styled.span`
  font-size: 11px; font-weight: 700; padding: 4px 8px; border-radius: 6px;
  background: ${p => {
      if(p.type.includes('Type 3')) return 'rgba(102,252,241,0.2)';
      if(p.type.includes('Terminal')) return 'rgba(239,68,68,0.2)';
      return 'rgba(234,179,8,0.2)';
  }};
  color: ${p => {
      if(p.type.includes('Type 3')) return '#66fcf1';
      if(p.type.includes('Terminal')) return '#ef4444';
      return '#eab308';
  }};
`;
const StatRow = styled.div`
  display: flex; justify-content: space-between; font-size: 13px; color: #8a9ba8;
  padding: 6px 0; border-bottom: 1px dashed #2a3441;
  &:last-child { border: none; }
  strong { color: #c5c6c7; }
`;
const LoadingDot = styled.div`
  text-align: center; padding: 60px; color: #45a29e; font-size: 16px;
  animation: ${pulse} 1.5s infinite;
`;

export default function WyckoffPage() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await apiService.getWyckoffSprings();
            if (res.success) setData(res.data);
        } catch (e) {
            console.error('Failed to load Wyckoff data', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    return (
        <PageWrap>
            <Header>
                <TitleInfo>
                    <Btn style={{border:'none', padding:0}} onClick={() => navigate('/labs')}>
                        <ArrowLeft size={20} color="#8a9ba8"/>
                    </Btn>
                    <Title><Target size={24} /> 와이코프 '스프링' 탐지기</Title>
                </TitleInfo>
                <Btn primary onClick={loadData} disabled={loading}>
                    <RefreshCw size={16} className={loading?"animate-spin":""} />
                    스캔 시작
                </Btn>
            </Header>

            <InfoBox>
                <HelpCircle size={20} color="#45a29e" style={{flexShrink:0, marginTop:2}}/>
                <div>
                    <strong>와이코프 '스프링(Spring)' 세계관이란?</strong>
                    <p>리버모어가 돌파(Breakout)를 노린다면, 와이코프는 박스권 하단에서 세력이 개미들의 손절 물량을 받아먹기 위해 고의로 지지선을 무너뜨리는 <b>'속임수 이탈(Spring)'</b>을 탐지합니다. <br/>
                    • <b>Type 3 (Low Vol)</b>: 거래량 없이 지지선을 이탈하는 경우 (물량이 씨가 마름)<br/>
                    • <b>Terminal Shakeout</b>: 대량 거래량을 동반하며 지지선을 깼으나 즉시 회복하는 강력한 털기<br/>
                    *현재 당일 거래대금 상위 100종목 중에서 지지를 이탈했다가 다시 박스권으로 회복중인 종목을 스캔합니다.</p>
                </div>
            </InfoBox>

            {loading ? (
                <LoadingDot>잠시만 기다려주세요, 와이코프 패턴을 분석 중입니다...</LoadingDot>
            ) : data.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#8a9ba8' }}>조건에 부합하는 스프링(Spring) 패턴 종목이 없습니다.</div>
            ) : (
                <CardGrid>
                    {data.map(item => (
                        <Card key={item.code} onClick={() => window.open(`/labs/stock/technical?symbol=${item.code}`, '_blank')}>
                            <CardHead>
                                <StockName>{item.name}</StockName>
                                <SpringTag type={item.springType}>{item.springType}</SpringTag>
                            </CardHead>
                            
                            <StatRow>
                                <span>현재가 (Price)</span>
                                <strong>{item.price.toLocaleString()}원</strong>
                            </StatRow>
                            <StatRow>
                                <span>박스권 하단 (TR Low)</span>
                                <strong>{item.trLow.toLocaleString()}원</strong>
                            </StatRow>
                            <StatRow>
                                <span>이탈 깊이 (Dip Depth)</span>
                                <strong style={{color:'#ef4444'}}>▼ {item.dipDepth}%</strong>
                            </StatRow>
                            <StatRow>
                                <span>거래량 비율 (Vol Ratio)</span>
                                <strong>{item.springVolRatio}x</strong>
                            </StatRow>
                            <StatRow>
                                <span>박스권 회복 (Recovered)</span>
                                <strong style={{color: item.isRecovered ? '#34d399' : '#eab308'}}>
                                    {item.isRecovered ? "성공 (Buy)" : "진행중 (Wait)"}
                                </strong>
                            </StatRow>
                        </Card>
                    ))}
                </CardGrid>
            )}
        </PageWrap>
    );
}
